import React from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  deploymentLabel,
  versionLabel,
} from './runDiagnosticsUtils';

type ValidationDialogMode = 'validate' | 'revalidate';

type Props = {
  open: boolean;
  run: any | null;
  validating?: boolean;
  mode?: ValidationDialogMode;
  onCancel: () => void;
  onConfirm: (additionalContext: string) => void;
};

const ValidateConfirmDialog: React.FC<Props> = ({
                                                  open,
                                                  run,
                                                  validating,
                                                  mode = 'validate',
                                                  onCancel,
                                                  onConfirm,
                                                }) => {
  const [additionalContext, setAdditionalContext] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setAdditionalContext('');
    }
  }, [open, run?.id, mode]);

  const isRevalidate = mode === 'revalidate';

  return (
    <Dialog open={open} onClose={validating ? undefined : onCancel} fullWidth maxWidth="md">
      <DialogTitle>
        {isRevalidate
          ? 'Regenerate and Revalidate Documentation?'
          : 'Validate and Improve Documentation?'}
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity={isRevalidate ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {isRevalidate
            ? 'This will use the existing validation failure context to regenerate the entity documentation, then validate the regenerated output.'
            : 'This will start a validation/regeneration cycle. The current output is validated first. If it fails or needs review, the validation feedback is used to regenerate the entity documentation, then the regenerated output is validated.'}
        </Alert>

        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography variant="body2">
              Entity:{' '}
              <b>
                {run?.entity_type}:{run?.entity_id}
              </b>
            </Typography>

            <Typography variant="body2">
              Resource/Section:{' '}
              <b>{run?.resource_type || run?.section_key || '-'}</b>
            </Typography>

            <Typography variant="body2">
              Version Scope:{' '}
              <b>
                {versionLabel(run)} / {deploymentLabel(run)}
              </b>
            </Typography>

            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              Generation Run ID: <b>{run?.id || run?.current_generation_run_id || '-'}</b>
            </Typography>

            {run?.root_generation_run_id ? (
              <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                Root Generation Run ID: <b>{run.root_generation_run_id}</b>
              </Typography>
            ) : null}
          </Stack>

          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={12}
            label="Additional Regeneration Context"
            placeholder="Optional: explain what should be corrected, what evidence to prioritize, expected images/sections, version constraints, reviewer notes, or any special instructions for the regenerated documentation..."
            value={additionalContext}
            onChange={(event) => setAdditionalContext(event.target.value)}
            helperText={
              isRevalidate
                ? 'This context is added to the existing validation feedback before regeneration.'
                : 'This context is included in the validation/regeneration cycle and can guide the regenerated output if the first validation fails.'
            }
          />

          <Typography variant="body2" color="text.secondary">
            After the request starts, this dialog closes and the generation run modal remains open so you can review
            the validation run, question results, regenerated output, and final validation state in one place.
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} disabled={validating}>
          Cancel
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={() => onConfirm(additionalContext)}
          disabled={validating || !(run?.id || run?.current_generation_run_id)}
        >
          {validating
            ? isRevalidate
              ? 'Regenerating...'
              : 'Validating...'
            : isRevalidate
              ? 'Regenerate & Revalidate'
              : 'Validate & Improve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidateConfirmDialog;